"use client";

import { useLingui } from "@lingui/react/macro";
import { WandSparklesIcon, XIcon } from "lucide-react";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { isAiPagePathname } from "../ai-pathname";
import { useAiSidePanel } from "../ai-side-panel-provider";
import { useWorkspaceSidePanelMode } from "../workspace-side-panel-mode";

/**
 * Amethyst AI のサイドピークトグルを描画する。
 *
 * 幅が限られるヘッダーでも AI 導線を残すため、狭い幅では短いラベルにする。
 */
export function AiSidePanelToggle() {
  const { isEnabled } = useAiSidePanel();
  const { isOpen, mode, openAiChat, closeWorkspaceSidePanel } =
    useWorkspaceSidePanelMode();
  const pathname = usePathname();
  const { i18n, t } = useLingui();
  const isAiPage = isAiPagePathname(pathname);
  const isActive = isOpen && mode === "AI_CHAT" && !isAiPage;
  const toggleLabel = isAiPage
    ? t`Amethyst AI is open`
    : isActive
      ? i18n._("Close Amethyst AI")
      : i18n._("Open Amethyst AI");
  const aiLabel = t`Amethyst AI`;
  const buttonClassName = cn(
    "gap-1.5 rounded-md px-2.5 font-semibold shadow-none transition-colors",
    "border-amethyst-primary/80 bg-background text-foreground ring-1 ring-amethyst-primary/15 hover:border-amethyst-primary hover:bg-amethyst-extra-light/70 hover:text-foreground",
    "dark:border-amethyst-light/80 dark:bg-input/20 dark:ring-amethyst-light/20 dark:hover:bg-amethyst-primary/20",
    isActive &&
      "border-transparent bg-amethyst-extra-light text-amethyst-primary ring-1 ring-amethyst-primary/15 hover:border-transparent hover:bg-amethyst-extra-light hover:text-amethyst-primary dark:border-transparent dark:bg-amethyst-primary/25 dark:text-amethyst-semi-light dark:ring-amethyst-light/25 dark:hover:bg-amethyst-primary/25 dark:hover:text-amethyst-semi-light",
    isAiPage &&
      "cursor-default border-border bg-muted/20 text-muted-foreground ring-0 hover:border-border hover:bg-muted/20 hover:text-muted-foreground dark:border-input dark:bg-input/10 dark:hover:bg-input/10",
  );

  if (!isEnabled) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          onClick={
            isAiPage
              ? undefined
              : isActive
                ? closeWorkspaceSidePanel
                : openAiChat
          }
          aria-label={toggleLabel}
          aria-pressed={isActive}
          aria-disabled={isAiPage}
          className={buttonClassName}
        >
          {isAiPage ? (
            <span
              aria-hidden="true"
              className="size-2 rounded-full bg-amethyst-primary ring-4 ring-amethyst-primary/10 dark:ring-amethyst-semi-light/20"
            />
          ) : null}
          <WandSparklesIcon
            className={cn(
              "size-4 text-amethyst-primary dark:text-amethyst-semi-light",
              isActive && "text-amethyst-primary dark:text-amethyst-semi-light",
              isAiPage && "text-muted-foreground dark:text-muted-foreground",
            )}
          />
          <span className="text-[11px] font-bold leading-none @lg:hidden">
            AI
          </span>
          <span className="hidden text-xs font-semibold leading-none @lg:inline">
            {aiLabel}
          </span>
          {isActive ? (
            <XIcon className="size-4 text-amethyst-primary dark:text-amethyst-semi-light" />
          ) : null}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{toggleLabel}</TooltipContent>
    </Tooltip>
  );
}
